import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import UserProfilePage from "../userProfile/UserProfilePage";
import RibbonsTransactionsPage from "../ribbonsTransactions/RibbonsTransactionsPage";
import TabPage from "../../../components/tab/tabPage";
import CommonPageLayout from "../../../components/commonPageLayout/commonPageLayout";
import CustomButton from "../../../components/CustomButton/Button";
import {
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import UserPointsTab from "../UserPoints/UserPointsPage";
import UserCreditsTab from "../userCredits/UserCreditsPage";
import QrCodePage from "../QrCode/QrCodePage";
import UserMembershipsTab from "../userMemberships/UserMembershipsPage";
import UserDevicesTab from "../userDevices/UserDevicesPage";
import UserCardPanels from "../userCardPanels/UserCardPanels";
import GroupedUserAccount from "../groupedUserAccounts";
import UserTagsPage from "../userTags/UserTagsPage";
import AppTrackingPage from "../appTracking/AppTrackingPage";
import UserGiftCardsPage from "../userGiftCards/UserGiftCardsPage";
import UserPaymentsPage from "../userPayments";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

interface UserData {
  userId: string;
  creationDate: string;
  lastLoginDate: string;
  email: string;
  firstName: string;
  surName: string;
  dateOfBirth: string;
  mobile: string;
  accountCode: string;
  associatedAccountCodes: string[];
  initialSigninProvider: string;
  existingSigninProviders: string[];
  currentSigninProvider: string;
  socialHandles: string[];
  address: string;
}
function UserDetailsPage() {
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const deepLinkTab = searchParams.get("tab");
  const defaultTabValue = deepLinkTab === "gc" ? "12" : "1";
  const [userDefaultData, setUserDefaultData] = useState<UserData | any>(null);
  const [userData, setUserData] = useState<UserData | any>(null);
  const [notification, setNotification] = useState<{
    type: "success" | "error" | any;
    message: string | null;
  }>({
    type: null,
    message: null
  });
  const [openResetPasswordModal, setOpenResetPasswordModal] = useState(false);
  const [openAnonymizeModal, setOpenAnonymizeModal] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [anonymizeLoading, setAnonymizeLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  const handleBackToUsers = () => {
    localStorage.setItem("isUserBackToUserSearchPage", "true");
    navigate("/users");
  };

  const getUserName = () => {
    return `${userDefaultData?.FirstName || ""} ${
      userDefaultData?.SurName || ""
    }`;
  };

  const handleResetPassword = async () => {
    if (!userId) return;
    try {
      setResetPasswordLoading(true);
      setNotification({ type: null, message: null });
      await axios.post(
        `${apiBaseUrl}/users/sendresetpasswordemail`,
        {
          Email: userDefaultData.Email
        },
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      setOpenResetPasswordModal(false);
      setNotification({
        type: "success",
        message: "Password reset link sent successfully"
      });
    } catch (err) {
      setNotification({
        type: "error",
        message: "Failed to send reset password link"
      });
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleAnonymize = async () => {
    if (!userId) return;
    try {
      setAnonymizeLoading(true);
      setNotification({ type: null, message: null });
      await axios.put(
        `${apiBaseUrl}/users/support/user/anonymize`,
        { UserId: userId },
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      setOpenAnonymizeModal(false);
      setNotification({
        type: "success",
        message: "User anonymized successfully"
      });
      await fetchUserData();
    } catch (err) {
      setNotification({ type: "error", message: "Failed to anonymize user" });
    } finally {
      setAnonymizeLoading(false);
    }
  };

  const fetchUserData = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiBaseUrl}/users/support/user/${userId}`,
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );

      setUserDefaultData(response.data);
      setUserData(response.data);
    } catch (err) {
      console.error("Failed to fetch user data:", err);
    } finally {
      setLoading(false);
    }
  };

  const scrollTabsToEnd = () => {
    const root = tabsRef.current;
    if (!root) return;
    const scroller = root.querySelector(".MuiTabs-scroller");
    if (scroller instanceof HTMLElement) {
      scroller.scrollLeft = scroller.scrollWidth;
    }
  };

  useEffect(() => {
    if (deepLinkTab === "gc") {
      // Wait a tick to ensure MUI has mounted and measured tabs
      requestAnimationFrame(() => {
        scrollTabsToEnd();
      });
    }
  }, [deepLinkTab]);

  const tabs = [
    {
      label: "Profile",
      value: "1",
      component: (
        <UserProfilePage
          userData={userData}
          setUserDetail={(data: any) => setUserData(data)}
          userDefaultData={userDefaultData}
          fetchUserData={fetchUserData}
          loading={loading}
          setNotification={(notification: any) => setNotification(notification)}
        />
      )
    },
    {
      label: "QR Code",
      value: "2",
      component: <QrCodePage userData={userData} />
    },
    {
      label: "Devices",
      value: "3",
      component: userId ? <UserDevicesTab userId={userId} /> : null
    },
    {
      label: "Ribbons",
      value: "4",
      component: <RibbonsTransactionsPage />
    },
    {
      label: "Memberships",
      value: "5",
      component: userId ? <UserMembershipsTab userId={userId} /> : null
    },
    {
      label: "Points",
      value: "6",
      component: userId ? <UserPointsTab userId={userId} /> : null
    },
    {
      label: "Credits",
      value: "7",
      component: userId ? <UserCreditsTab userId={userId} /> : null
    },
    {
      label: "CARD PANELS",
      value: "8",
      component: userId ? <UserCardPanels /> : null
    },
    {
      label: "GROUPS",
      value: "9",
      component: userId ? <GroupedUserAccount /> : null
    },
    {
      label: "TAGS",
      value: "10",
      component: userId ? <UserTagsPage /> : null
    },
    {
      label: "App Tracking",
      value: "11",
      component: userId ? <AppTrackingPage userId={userId} /> : null
    },
    {
      label: "Gift Cards",
      value: "12",
      component: userId ? <UserGiftCardsPage /> : null
    },
    {
      label: "Payments",
      value: "13",
      component: userId ? <UserPaymentsPage /> : null
    }
  ];

  const docsContent = (
    <Box display="flex" gap={2} alignItems="center" justifyContent="flex-end">
      <CustomButton callBack={() => setOpenAnonymizeModal(true)}>
        Anonymize
      </CustomButton>
      {/* <CustomButton callBack={() => setOpenResetPasswordModal(true)}>
        Send Reset Pwd Link
      </CustomButton> */}
      <CustomButton callBack={handleBackToUsers}>Back</CustomButton>
    </Box>
  );

  return (
    <>
      <CommonPageLayout
        heading={getUserName()}
        subHeading="Manage User"
        docs={docsContent}
      >
        {notification.message && (
          <Alert
            severity={notification.type || "info"}
            sx={{ mb: 2 }}
            onClose={() => setNotification({ type: null, message: null })}
          >
            {notification.message}
          </Alert>
        )}
        <TabPage
          tabsRef={tabsRef}
          tabs={tabs}
          defaultTab={defaultTabValue}
          showRefresh={false}
          onTabChange={(val) => {
            if (val === "12") {
              requestAnimationFrame(() => {
                scrollTabsToEnd();
              });
            } else {
              // If navigating away from Gift Cards tab, remove any existing query params
              if (location.search) {
                navigate(location.pathname, { replace: true });
              }
            }
          }}
        />
      </CommonPageLayout>

      {/* Reset Password Confirmation Modal */}
      <Dialog
        open={openResetPasswordModal}
        onClose={() =>
          !resetPasswordLoading && setOpenResetPasswordModal(false)
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: 1.25,
                fontFamily: "Montserrat",
                fontStyle: "normal",
                color: "#192F67"
              }}
            >
              Confirm Password Reset
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() =>
                !resetPasswordLoading && setOpenResetPasswordModal(false)
              }
              disabled={resetPasswordLoading}
              sx={{
                color: (theme) =>
                  resetPasswordLoading
                    ? theme.palette.grey[400]
                    : theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to send a password reset link to this user?
            This will allow them to set a new password.
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton
            callBack={() =>
              !resetPasswordLoading && setOpenResetPasswordModal(false)
            }
            disabled={resetPasswordLoading}
          >
            Cancel
          </CustomButton>
          <CustomButton
            callBack={handleResetPassword}
            disabled={resetPasswordLoading}
            styles={{ minHeight: "36px" }}
          >
            {resetPasswordLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Send Reset Link"
            )}
          </CustomButton>
        </DialogActions>
      </Dialog>

      {/* Anonymize Confirmation Modal */}
      <Dialog
        open={openAnonymizeModal}
        onClose={() => !anonymizeLoading && setOpenAnonymizeModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: 1.25,
                fontFamily: "Montserrat",
                fontStyle: "normal",
                color: "#192F67"
              }}
            >
              Confirm Anonymization
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => !anonymizeLoading && setOpenAnonymizeModal(false)}
              disabled={anonymizeLoading}
              sx={{
                color: (theme) =>
                  anonymizeLoading
                    ? theme.palette.grey[400]
                    : theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to anonymize this user? This action cannot be
            undone and will remove all personal information.
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton
            callBack={() => !anonymizeLoading && setOpenAnonymizeModal(false)}
            disabled={anonymizeLoading}
          >
            Cancel
          </CustomButton>
          <CustomButton
            callBack={handleAnonymize}
            disabled={anonymizeLoading}
            styles={{ minHeight: "36px" }}
          >
            {anonymizeLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Anonymize"
            )}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default UserDetailsPage;
